import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import MultiStepForm from './components/MultiStepForm';
import TreatmentConsents from './components/TreatmentConsents';
import MedicalRecordsRelease from './components/MedicalRecordsRelease';
import SelfPayFinancialPolicy from './components/SelfPayFinancialPolicy';
import IncomingReferralForm from './components/IncomingReferralForm';
import ContactForm from './components/ContactForm';
import EstimateSharePage from './components/public/EstimateSharePage';
import PostoperativeContactSharePage from './components/public/PostoperativeContactSharePage';
import ReceiptSharePage from './components/public/ReceiptSharePage';
import SharePage from './components/public/SharePage';
import LanguageSwitcher from './components/LanguageSwitcher';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';

function App() {
  const { t } = useTranslation('common');
  const [serverVersion, setServerVersion] = useState('');
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isConsent = path === '/rxconsent';
  const isIntake = path === '/intake';
  const isRecordsRelease = path === '/recordsrelease';
  const isSelfPayPolicy = path === '/selfpay';
  const isReferral = path === '/referral';
  const isContact = path === '/contact';

  // Detect estimate share token: root-level path with 10 alphanumeric chars (e.g. /AbCd123456)
  const shareTokenMatch = path.match(/^\/([A-Za-z0-9]{10})$/);
  const isEstimateShare = !!shareTokenMatch;
  const estimateToken = shareTokenMatch ? shareTokenMatch[1] : null;
  const postoperativeContactMatch = path.match(/^\/([A-Za-z0-9]{22})\/?$/)
    || path.match(/^\/surgery-contact\/([A-Za-z0-9]{22}|[A-Za-z0-9_-]{43})\/?$/);
  const isPostoperativeContactShare = !!postoperativeContactMatch;
  const postoperativeContactToken = postoperativeContactMatch ? postoperativeContactMatch[1] : null;
  // Detect a payment-receipt short link: root-level path with 8 alphanumeric
  // chars (e.g. strz.me/ufvhiuag) — a different length than the estimate
  // (10) and postop-contact (22) tokens above, so there's no ambiguity there.
  // "/referral" is also exactly 8 characters though, so it's explicitly
  // excluded to avoid shadowing that named route.
  const receiptShareMatch = !isReferral ? path.match(/^\/([A-Za-z0-9]{8})$/) : null;
  const isReceiptShare = !!receiptShareMatch;
  const receiptShareToken = receiptShareMatch ? receiptShareMatch[1] : null;
  // Detect a billing-document share link (invoice / payment receipt):
  // root-level path with 9 alphanumeric chars — distinct from receipt (8),
  // estimate (10), and postop-contact (22) tokens. "/rxconsent" is exactly
  // 9 characters, so it's explicitly excluded (same collision "/referral"
  // had with the 8-char receipt tokens above).
  const docShareMatch = !isConsent ? path.match(/^\/([A-Za-z0-9]{9})$/) : null;
  const isDocShare = !!docShareMatch;
  const docShareToken = docShareMatch ? docShareMatch[1] : null;

  useEffect(() => {
    // Try to fetch the backend version so UI reflects deployed backend
    if (isEstimateShare || isPostoperativeContactShare || isReceiptShare || isDocShare) return; // Skip version fetch for public share pages
    const fetchVersion = async () => {
      try {
        const isLocal3000 = typeof window !== 'undefined' && window.location && window.location.port === '3000';
        const base = process.env.REACT_APP_API_BASE || (isLocal3000 ? 'http://localhost:3001' : '');
        const res = await fetch(`${base}/version`);
        if (res.ok) {
          const data = await res.json();
          if (data?.version) {
            setServerVersion(data.version);
          }
        }
      } catch (e) {
        // Ignore errors; we'll fall back to build-time version
        if (process.env.NODE_ENV !== 'production') {
          // Helpful during local dev
          // eslint-disable-next-line no-console
          console.warn('Failed to fetch /version from API. Falling back to build-time version.', e);
        }
      }
    };
    fetchVersion();
  }, [isEstimateShare, isPostoperativeContactShare, isReceiptShare, isDocShare]);

  // Public share pages render full-width (no Container wrapper)
  if (isPostoperativeContactShare) {
    return <PostoperativeContactSharePage token={postoperativeContactToken} />;
  }
  if (isEstimateShare) {
    return <EstimateSharePage token={estimateToken} />;
  }
  if (isReceiptShare) {
    return <ReceiptSharePage token={receiptShareToken} />;
  }
  if (isDocShare) {
    return <SharePage token={docShareToken} />;
  }
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {path === '/' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <LanguageSwitcher />
          </Box>
          <Typography variant="h4" align="center" gutterBottom>
            {t('portal.title')}
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('portal.newPatientsTitle')}
            </Typography>
            <Typography variant="body2">
              {t('portal.newPatientsDescription')}
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              {t('portal.requiredForNewPatients')}
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Button href="/intake" variant="contained" size="large" fullWidth>
                  {t('portal.patientIntakeForm')}
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, px: 1, color: 'text.secondary' }}>
                  {t('portal.patientIntakeDescription')}
                </Typography>
              </Box>
              <Box>
                <Button href="/rxconsent" variant="contained" color="success" size="large" fullWidth>
                  {t('portal.treatmentConsents')}
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, px: 1, color: 'text.secondary' }}>
                  {t('portal.treatmentConsentsDescription')}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="h6" gutterBottom color="text.secondary">
              {t('portal.additionalForms')}
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Button href="/recordsrelease" variant="contained" color="info" size="large" fullWidth>
                  {t('portal.medicalRecordsRelease')}
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, px: 1, color: 'text.secondary' }}>
                  {t('portal.medicalRecordsDescription')}
                </Typography>
              </Box>
              <Box>
                <Button href="/selfpay" variant="contained" color="warning" size="large" fullWidth>
                  {t('portal.selfPayPolicy')}
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, px: 1, color: 'text.secondary' }}>
                  {t('portal.selfPayDescription')}
                </Typography>
              </Box>
              <Box>
                <Button href="/referral" variant="contained" color="secondary" size="large" fullWidth>
                  {t('portal.incomingReferral')}
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, px: 1, color: 'text.secondary' }}>
                  {t('portal.referralDescription')}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </>
      )}
      {isIntake && (
        <>
          <Typography variant="h4" align="center" gutterBottom>
            {t('portal.patientQuestionnaire')}
          </Typography>
          <MultiStepForm />
        </>
      )}
      {isConsent && (
        <>
          <Typography variant="h4" align="center" gutterBottom>
            {t('portal.treatmentConsents')}
          </Typography>
          <TreatmentConsents />
        </>
      )}
      {isRecordsRelease && <MedicalRecordsRelease />}
      {isSelfPayPolicy && <SelfPayFinancialPolicy />}
      {isReferral && <IncomingReferralForm />}
      {isContact && <ContactForm />}
      <Typography variant="caption" display="block" align="center" sx={{ mt: 4, color: 'text.secondary' }}>
        Version: {serverVersion || process.env.REACT_APP_VERSION || 'dev'}
      </Typography>
    </Container>
  );
}

export default App;
